import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer, View, SlotInfo } from 'react-big-calendar';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../app/stores/store';
import { observer } from 'mobx-react-lite';
import { CalendarEventDTO } from '../../app/models/Calendar';
import { Icon, Label, Grid, Statistic, Header, Dropdown, Button, Form, Loader, Message, Modal, Table, Segment } from 'semantic-ui-react';
import CustomDateTimePicker from '../../app/common/form/CustomDateTimePicker';
import { createSportOptions } from '../../app/common/options/sportOptions';
import {
  calculateOccupancyPercentage,
  getOccupancyColor,
  getOccupancyLabel,
  formatCapacityDisplay
} from '../../app/common/util/capacityUtils';
import { formatDateDisplay } from '../../app/common/util/dateUtils';
import CapacitySelector from './CapacitySelector';
import PriceCalculator from './PriceCalculator';
import PaymentStatusSelector from './PaymentStatusSelector';
import RecurringSeriesModal from './RecurringSeriesModal';
import RecurringActionDialog from './RecurringActionDialog';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

export type CalendarStatusOption = {
  value: string;
  label: string;
  color: string;
  icon?: string;
};

interface EventFormData {
  companySportAssetVenueId: string;
  dateTimeStart: Date;
  dateTimeEnd: Date;
  capacityUnitsRequested: number;
  customerFullName: string;
  customerPhone: string;
  customerEmail: string;
  note: string;
  isPaid: number;
  statusId: string;
  manualPriceOverride: number | null;
}

interface FilterState {
  venues: string[];
  assetTypes: string[];
  companies: string[];
  assets: string[];
}

const SportAssetCalendar: React.FC = observer(() => {
  const { t } = useTranslation();
  const { calendarStore, companySportVenueAssetStore, companyVenueStore } = useStore();

  const [view, setView] = useState<View>('week');
  const [date, setDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventDTO | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [bookingDuration, setBookingDuration] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [capacityManuallyChanged, setCapacityManuallyChanged] = useState(false);

  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    assetId: string;
    startTime: Date;
    endTime: Date;
  } | null>(null);

  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringInitialAssetId, setRecurringInitialAssetId] = useState<string | undefined>();
  const [recurringInitialSlotStart, setRecurringInitialSlotStart] = useState<Date | undefined>();
  const [recurringInitialSlotEnd, setRecurringInitialSlotEnd] = useState<Date | undefined>();

  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [recurringDialogEvent, setRecurringDialogEvent] = useState<CalendarEventDTO | null>(null);
  const [recurringDialogIntent, setRecurringDialogIntent] = useState<'edit' | 'delete'>('edit');

  const [pendingDetachEventId, setPendingDetachEventId] = useState<string | null>(null);

  const [eventForm, setEventForm] = useState<EventFormData>({
    companySportAssetVenueId: '',
    dateTimeStart: new Date(),
    dateTimeEnd: new Date(),
    capacityUnitsRequested: 0,
    customerFullName: '',
    customerPhone: '',
    customerEmail: '',
    note: '',
    isPaid: 0,
    statusId: '',
    manualPriceOverride: null,
  });

  const [filters, setFilters] = useState<FilterState>({
    venues: [],
    assetTypes: [],
    companies: [],
    assets: []
  });

  const localizedCalendarStatusesOptions = useMemo((): CalendarStatusOption[] => [
    { value: '2d8f5ab1-5882-44a5-898e-ec0c6dccf163', label: t('calendarStatuses.available'), color: '#28a745', icon: 'check-circle' },
    { value: '6c8b45c2-2a2d-4327-8255-2ebb35623017', label: t('calendarStatuses.booked'), color: '#007bff', icon: 'calendar' },
    { value: '597227a2-b5a5-436c-b52e-329fc6774455', label: t('calendarStatuses.paid'), color: '#17a2b8', icon: 'credit-card' },
    { value: '84a33d72-f62c-4f9d-bda6-2c4abb6cfdcc', label: t('calendarStatuses.pending'), color: '#ffc107', icon: 'hourglass-half' },
    { value: 'cb1a2b6d-31cd-4348-91fd-1bfb51a4f3d8', label: t('calendarStatuses.cancelled'), color: '#dc3545', icon: 'times-circle' },
    { value: '67b6a0d9-413d-4cd8-8f33-41bf870108aa', label: t('calendarStatuses.undermaintenance'), color: '#6c757d', icon: 'tools' },
    { value: 'ce213a4f-e36e-47c4-bc14-af4c7c1daab2', label: t('calendarStatuses.blocked'), color: '#343a40', icon: 'ban' }
  ], [t]);

  const events = calendarStore.events;
  const resources = calendarStore.resources;
  const loading = calendarStore.loading;
  const { loadAllVenues } = companyVenueStore;
  const { loadAllAssets } = companySportVenueAssetStore;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadAllVenues();
    loadAllAssets();
  }, [loadAllVenues, loadAllAssets]);

  useEffect(() => {
    const loadCalendarData = async () => {
      const startDate = new Date(date.getTime() - 7 * 24 * 60 * 60 * 1000);
      const endDate = new Date(date.getTime() + 30 * 24 * 60 * 60 * 1000);
      try {
        await calendarStore.loadCalendarDataWithDateRange({ startDate, endDate });
      } catch (error) {
        console.error('Error loading calendar data:', error);
      }
    };
    loadCalendarData();
  }, [calendarStore, date]);

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) setIsFullscreen(false);
        else if (showDeleteConfirm) setShowDeleteConfirm(false);
        else if (showEventModal) { setShowEventModal(false); resetForm(); calendarStore.clearPriceCalculation(); }
        else if (showCapacityModal) setShowCapacityModal(false);
        else if (showRecurringModal) setShowRecurringModal(false);
        else if (showRecurringDialog) { setShowRecurringDialog(false); setRecurringDialogEvent(null); }
      }
    };
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showEventModal, showDeleteConfirm, isFullscreen, showCapacityModal, showRecurringModal, showRecurringDialog, calendarStore]);

  const filteredResources = useMemo(() => {
    if (!resources.length) return [];
    let filteredRes = resources.filter(r => r.isActive && r.isBookable);
    if (filters.venues.length > 0) filteredRes = filteredRes.filter(r => filters.venues.includes(r.venueName || ''));
    if (filters.assetTypes.length > 0) filteredRes = filteredRes.filter(r => r.sports.some(s => filters.assetTypes.includes(s)));
    if (filters.companies.length > 0) filteredRes = filteredRes.filter(r => filters.companies.includes(r.companyName || ''));
    if (filters.assets.length > 0) filteredRes = filteredRes.filter(r => filters.assets.includes(r.id));
    return filteredRes;
  }, [resources, filters]);

  const formats = useMemo(() => ({
    timeGutterFormat: (date: Date) => moment(date).format('HH:mm'),
    eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) => `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
    agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) => `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
    dayHeaderFormat: (date: Date) => moment(date).format('dddd MMM DD'),
    dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) => `${moment(start).format('MMM DD')} - ${moment(end).format('MMM DD')}`,
  }), []);

  const getTimeSlotOccupancy = useCallback((assetId: string, startTime: Date, endTime: Date) => {
    return calendarStore.getTimeSlotOccupancy(assetId, startTime, endTime);
  }, [calendarStore]);

  const calendarResources = useMemo(() => {
    return filteredResources.map(resource => {
      const sportsText = resource.sports?.length > 0
        ? resource.sports.map(sport => t(`sports.${sport.toLowerCase()}`) || sport).join(', ')
        : '';
      return {
        resourceId: resource.id,
        resourceTitle: (
          <div style={{ padding: '4px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{resource.name}</div>
            <div style={{ fontSize: '12px', color: '#6c757d' }}>{resource.venueName}</div>
            {resource.companyName && <div style={{ fontSize: '11px', color: '#6c757d' }}>{resource.companyName}</div>}
            {sportsText && <div style={{ fontSize: '11px', color: '#007bff', marginTop: '2px' }}>{sportsText}</div>}
            <div style={{ fontSize: '11px', color: '#28a745', marginTop: '2px' }}>
              <Icon name="grid layout" size="small" />
              {resource.totalCapacityUnits} {t('capacity.units')}
            </div>
          </div>
        )
      };
    });
  }, [filteredResources, t]);

  const aggregatedCalendarEvents = useMemo(() => {
    const result: any[] = [];
    const eventsByAssetAndTime = new Map<string, CalendarEventDTO[]>();

    events.forEach(event => {
      const key = `${event.resourceId}_${event.start.getTime()}_${event.end.getTime()}`;
      if (!eventsByAssetAndTime.has(key)) eventsByAssetAndTime.set(key, []);
      eventsByAssetAndTime.get(key)!.push(event);
    });

    eventsByAssetAndTime.forEach((slotEvents) => {
      const firstEvent = slotEvents[0];
      const resource = resources.find(r => r.id === firstEvent.resourceId);
      if (!resource) return;

      const totalUsedCapacity = slotEvents.reduce((sum, e) => sum + (e.capacityUsed || 0), 0);
      const occupancyPercentage = calculateOccupancyPercentage(totalUsedCapacity, resource.totalCapacityUnits);
      const occupancyColor = getOccupancyColor(occupancyPercentage);
      const hasRecurring = slotEvents.some(e => e.recurringSeriesId && !e.isDetachedFromSeries);
      const isAggregated = slotEvents.length > 1;
      const key = `${firstEvent.resourceId}_${firstEvent.start.getTime()}_${firstEvent.end.getTime()}`;

      result.push({
        ...firstEvent,
        id: key,
        title: isAggregated ? `${slotEvents.length} ${t('calendar.bookings')}` : (slotEvents[0].customerFullName || t('calendar.booking')),
        resource: firstEvent.resourceId,
        usedCapacity: totalUsedCapacity,
        totalCapacity: resource.totalCapacityUnits,
        occupancyPercentage,
        occupancyColor,
        bookings: slotEvents,
        isAggregated,
        hasRecurringInGroup: hasRecurring,
        recurringSeriesId: isAggregated ? undefined : firstEvent.recurringSeriesId,
        isDetachedFromSeries: isAggregated ? undefined : firstEvent.isDetachedFromSeries,
      });
    });

    return result;
  }, [events, resources, t]);

  const uniqueVenues = useMemo(() => Array.from(new Set(resources.map(r => r.venueName).filter(Boolean))), [resources]);
  const uniqueSports = useMemo(() => Array.from(new Set(resources.flatMap(r => r.sports || []))).map(sport => ({ value: sport, label: sport })), [resources]);
  const uniqueCompanies = useMemo(() => Array.from(new Set(resources.map(r => r.companyName).filter(Boolean))), [resources]);

  const durationOptions = useMemo(() => [
    { key: 0.5, value: 0.5, text: t('calendars.durations.30minutes') },
    { key: 1, value: 1, text: t('calendars.durations.1hour') },
    { key: 1.5, value: 1.5, text: t('calendars.durations.1.5hours') },
    { key: 2, value: 2, text: t('calendars.durations.2hours') },
    { key: 2.5, value: 2.5, text: t('calendars.durations.2.5hours') },
    { key: 3, value: 3, text: t('calendars.durations.3hours') },
    { key: 4, value: 4, text: t('calendars.durations.4hours') }
  ], [t]);

  const venueOptions = useMemo(() => uniqueVenues.map(v => ({ key: v, value: v, text: v })), [uniqueVenues]);
  const sportOptions = useMemo(() => createSportOptions(t, uniqueSports).map(s => ({ key: s.value, value: s.value, text: s.label })), [uniqueSports, t]);
  const companyOptions = useMemo(() => uniqueCompanies.map(c => ({ key: c, value: c, text: c })), [uniqueCompanies]);

  const buildDefaultNote = useCallback((durationHours: number) => t('calendars.defaultBookingNote', { duration: durationHours }), [t]);

  const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
    if (!selectionMode) return;
    const { start, resourceId } = slotInfo;
    const slotStart = new Date(start);
    const slotEnd = new Date(slotStart.getTime() + bookingDuration * 60 * 60 * 1000);
    const resource = resources.find(r => r.id === resourceId);
    if (!resource) return;

    const occupancy = getTimeSlotOccupancy(resourceId as string, slotStart, slotEnd);
    const available = resource.totalCapacityUnits - (occupancy?.usedCapacity || 0);
    if (available <= 0) { alert(t('capacity.noCapacityAvailable')); return; }

    setEventForm({
      companySportAssetVenueId: resourceId as string || '',
      dateTimeStart: slotStart,
      dateTimeEnd: slotEnd,
      capacityUnitsRequested: 0,
      customerFullName: '',
      customerPhone: '',
      customerEmail: '',
      note: buildDefaultNote(bookingDuration),
      isPaid: 0,
      statusId: selectedStatus || '',
      manualPriceOverride: null,
    });
    setSelectedEvent(null);
    setIsEditMode(false);
    setShowEventModal(true);
  }, [selectionMode, bookingDuration, selectedStatus, t, resources, getTimeSlotOccupancy, buildDefaultNote]);

  const handleAddAnotherBooking = useCallback((assetId: string, startTime: Date, endTime: Date) => {
    const resource = resources.find(r => r.id === assetId);
    if (!resource) return;
    const occupancy = getTimeSlotOccupancy(assetId, startTime, endTime);
    const available = resource.totalCapacityUnits - (occupancy?.usedCapacity || 0);
    if (available <= 0) { alert(t('capacity.noCapacityAvailable')); return; }

    const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
    setEventForm({
      companySportAssetVenueId: assetId,
      dateTimeStart: startTime,
      dateTimeEnd: endTime,
      capacityUnitsRequested: 0,
      customerFullName: '',
      customerPhone: '',
      customerEmail: '',
      note: buildDefaultNote(durationHours),
      isPaid: 0,
      statusId: selectedStatus || '',
      manualPriceOverride: null,
    });
    setSelectedEvent(null);
    setIsEditMode(false);
    setShowEventModal(true);
  }, [resources, getTimeSlotOccupancy, t, buildDefaultNote, selectedStatus]);

  const openEditForm = useCallback((booking: CalendarEventDTO) => {
    setCapacityManuallyChanged(false);
    setSelectedEvent(booking);
    setEventForm({
      companySportAssetVenueId: booking.resourceId,
      dateTimeStart: new Date(booking.start),
      dateTimeEnd: new Date(booking.end),
      capacityUnitsRequested: booking.capacityUsed || 0,
      customerFullName: booking.customerFullName || '',
      customerPhone: booking.customerPhone || '',
      customerEmail: booking.customerEmail || '',
      note: booking.note || buildDefaultNote(1),
      isPaid: booking.isPaid || 0,
      statusId: booking.statusId || '',
      manualPriceOverride: booking.manualPriceOverride ?? null,
    });
    setIsEditMode(true);
    setShowEventModal(true);
  }, [buildDefaultNote]);

  const handleSelectEvent = useCallback((event: any) => {
    const bookings: CalendarEventDTO[] = event.bookings ?? [event];

    if (bookings.length > 1) {
      setSelectedTimeSlot({ assetId: event.resource || event.resourceId, startTime: new Date(event.start), endTime: new Date(event.end) });
      setShowCapacityModal(true);
      return;
    }

    const rawEvent: CalendarEventDTO = bookings[0];
    const liveEvent = calendarStore.events.find(e => e.id === rawEvent.id);
    const isDetached = liveEvent?.isDetachedFromSeries ?? rawEvent.isDetachedFromSeries;

    if (rawEvent.recurringSeriesId && !isDetached) {
      setRecurringDialogEvent(rawEvent);
      setRecurringDialogIntent('edit');
      setShowRecurringDialog(true);
      return;
    }

    setSelectedTimeSlot({ assetId: event.resource || event.resourceId, startTime: new Date(event.start), endTime: new Date(event.end) });
    setShowCapacityModal(true);
  }, [calendarStore.events]);

  const handleSaveEvent = useCallback(async () => {
    if (pendingDetachEventId && isEditMode && selectedEvent) {
      const ok = await calendarStore.detachEventFromSeries(pendingDetachEventId);
      if (!ok) return;
      setPendingDetachEventId(null);
    }
    try {
      if (isEditMode && selectedEvent) {
        await calendarStore.updateEvent({
          id: selectedEvent.id,
          dateTimeStart: eventForm.dateTimeStart,
          dateTimeEnd: eventForm.dateTimeEnd,
          capacityUnitsRequested: eventForm.capacityUnitsRequested,
          customerFullName: eventForm.customerFullName,
          customerPhone: eventForm.customerPhone,
          customerEmail: eventForm.customerEmail,
          note: eventForm.note,
          isPaid: eventForm.isPaid,
          statusId: eventForm.statusId,
          manualPriceOverride: eventForm.manualPriceOverride,
        });
      } else {
        await calendarStore.createEvent({
          companySportVenueAssetId: eventForm.companySportAssetVenueId,
          dateTimeStart: eventForm.dateTimeStart,
          dateTimeEnd: eventForm.dateTimeEnd,
          capacityUnitsRequested: eventForm.capacityUnitsRequested,
          customerFullName: eventForm.customerFullName,
          customerPhone: eventForm.customerPhone,
          customerEmail: eventForm.customerEmail,
          note: eventForm.note,
          isPaid: eventForm.isPaid,
          statusId: eventForm.statusId,
          manualPriceOverride: eventForm.manualPriceOverride,
        });
      }
      setShowEventModal(false);
      resetForm();
      calendarStore.clearPriceCalculation();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  }, [eventForm, isEditMode, selectedEvent, pendingDetachEventId, calendarStore]);

  const handleDeleteEvent = useCallback(async () => {
    if (selectedEvent) {
      try {
        await calendarStore.deleteEvent(selectedEvent.id);
        setShowEventModal(false);
        setShowDeleteConfirm(false);
        resetForm();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  }, [selectedEvent, calendarStore]);

  const resetForm = useCallback(() => {
    setEventForm({ companySportAssetVenueId: '', dateTimeStart: new Date(), dateTimeEnd: new Date(), capacityUnitsRequested: 0, customerFullName: '', customerPhone: '', customerEmail: '', note: '', isPaid: 0, statusId: '', manualPriceOverride: null });
    setSelectedEvent(null);
    setIsEditMode(false);
    setShowDeleteConfirm(false);
    setCapacityManuallyChanged(false);
    setPendingDetachEventId(null);
  }, []);

  const clearFilters = useCallback(() => setFilters({ venues: [], assetTypes: [], companies: [], assets: [] }), []);
  const toggleSelectionMode = useCallback(() => setSelectionMode(prev => !prev), []);
  const toggleFullscreen = useCallback(() => setIsFullscreen(prev => !prev), []);

  const calendarMessages = useMemo(() => ({
    date: t('calendars.bigCalendar.date'),
    time: t('calendars.bigCalendar.time'),
    event: t('calendars.bigCalendar.event'),
    allDay: t('calendars.bigCalendar.allDay'),
    week: t('calendars.bigCalendar.week'),
    work_week: t('calendars.bigCalendar.workWeek'),
    day: t('calendars.bigCalendar.day'),
    month: t('calendars.bigCalendar.month'),
    previous: t('calendars.navigation.previous'),
    next: t('calendars.navigation.next'),
    yesterday: t('calendars.bigCalendar.yesterday'),
    tomorrow: t('calendars.bigCalendar.tomorrow'),
    today: t('calendars.navigation.today'),
    agenda: t('calendars.bigCalendar.agenda'),
    noEventsInRange: t('calendars.bigCalendar.noEventsInRange'),
    showMore: (total: number) => t('calendars.bigCalendar.showMore', { count: total })
  }), [t]);

  const eventStyleGetter = useCallback((event: any) => ({
    style: {
      backgroundColor: event.occupancyColor || '#007bff',
      borderRadius: '5px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'block'
    }
  }), []);

  const CustomEvent = useCallback(({ event }: { event: any }) => {
    const isAggregated = event.isAggregated || event.bookings?.length > 1;
    const showRecurringBadge =
      (!isAggregated && event.recurringSeriesId && !event.isDetachedFromSeries) ||
      (isAggregated && event.hasRecurringInGroup);

    return (
      <div style={{ padding: '2px 4px', width: '100%', height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2px', minWidth: 0 }}>
          <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1, lineHeight: '1.2' }}>
            {event.title}
          </strong>
          {isAggregated && <Icon name="users" size="small" style={{ flexShrink: 0, marginLeft: '2px' }} />}
          {showRecurringBadge && <Icon name="repeat" size="small" style={{ flexShrink: 0, marginLeft: '2px', opacity: 0.85 }} title={t('recurring.eventBadge.tooltip')} />}
        </div>
        {event.usedCapacity !== undefined && event.totalCapacity !== undefined && (
          <div style={{ fontSize: '0.75em', opacity: 0.9, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Icon name="grid layout" size="small" />
            {formatCapacityDisplay(event.usedCapacity, event.totalCapacity, t)} ({event.occupancyPercentage}%)
          </div>
        )}
        {event.customerPhone && !isAggregated && (
          <div style={{ fontSize: '0.75em', opacity: 0.9, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {event.customerPhone}
          </div>
        )}
      </div>
    );
  }, [t]);

  const selectedResource = useMemo(() => {
    if (!eventForm.companySportAssetVenueId) return null;
    return resources.find(r => r.id === eventForm.companySportAssetVenueId) || null;
  }, [eventForm.companySportAssetVenueId, resources]);

  const availableCapacity = useMemo(() => {
    if (!selectedResource || !eventForm.dateTimeStart || !eventForm.dateTimeEnd) return undefined;
    const occupancy = getTimeSlotOccupancy(eventForm.companySportAssetVenueId, eventForm.dateTimeStart, eventForm.dateTimeEnd);
    if (!occupancy) return selectedResource.totalCapacityUnits;
    let used = occupancy.usedCapacity;
    if (isEditMode && selectedEvent) used = used - (selectedEvent.capacityUsed || 0);
    return Math.max(0, selectedResource.totalCapacityUnits - used);
  }, [selectedResource, eventForm.companySportAssetVenueId, eventForm.dateTimeStart, eventForm.dateTimeEnd, getTimeSlotOccupancy, isEditMode, selectedEvent]);

  const formValid =
    eventForm.customerPhone.trim().length > 0 &&
    eventForm.customerEmail.trim().length > 0 &&
    eventForm.note.trim().length > 0;

  // Navigation helper
  const navigateDate = useCallback((direction: -1 | 1) => {
    const newDate = new Date(date);
    if (view === 'month') newDate.setMonth(date.getMonth() + direction);
    else if (view === 'week') newDate.setDate(date.getDate() + direction * 7);
    else newDate.setDate(date.getDate() + direction);
    setDate(newDate);
  }, [date, view]);

  const dateLabel = useMemo(() => {
    if (view === 'month') return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const start = new Date(date);
      start.setDate(date.getDate() - date.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }, [date, view]);

  if (loading && !events.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader active size="large">{t('loading.default')}</Loader>
      </div>
    );
  }

  // ─── Shared nav controls  ────
  const NavControls = ({ compact = false }: { compact?: boolean }) => (
    <div style={{ display: 'flex', gap: compact ? '0.25rem' : '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <Button color='blue' size={compact ? 'small' : 'medium'} onClick={() => navigateDate(-1)} icon='chevron left' />
      <Button color='green' size={compact ? 'small' : 'medium'} onClick={() => setDate(new Date())} content={t('calendars.navigation.today')} />
      <Button color='blue' size={compact ? 'small' : 'medium'} onClick={() => navigateDate(1)} icon='chevron right' />
      <Button color='teal' size={compact ? 'small' : 'medium'} onClick={() => { setRecurringInitialAssetId(undefined); setRecurringInitialSlotStart(undefined); setRecurringInitialSlotEnd(undefined); setShowRecurringModal(true); }} icon='repeat' title={t('recurring.button.openModal')} />
      <Button color={isFullscreen ? 'red' : 'blue'} size={compact ? 'small' : 'medium'} onClick={toggleFullscreen} icon={isFullscreen ? 'compress' : 'expand'} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} />
    </div>
  );

  const ViewButtons = ({ compact = false }: { compact?: boolean }) => (
    <div style={{ display: 'flex', gap: compact ? '0.25rem' : '0.5rem' }}>
      <Button size={compact ? 'small' : 'medium'} color={view === 'day' ? 'blue' : 'grey'} onClick={() => setView('day')} content={t('calendars.bigCalendar.day')} />
      <Button size={compact ? 'small' : 'medium'} color={view === 'week' ? 'blue' : 'grey'} onClick={() => setView('week')} content={t('calendars.bigCalendar.week')} />
      <Button size={compact ? 'small' : 'medium'} color={view === 'month' ? 'blue' : 'grey'} onClick={() => setView('month')} content={t('calendars.bigCalendar.month')} />
    </div>
  );

  const CalendarComponent = () => (
    <Calendar
      localizer={localizer}
      events={aggregatedCalendarEvents}
      resources={calendarResources}
      resourceIdAccessor="resourceId"
      resourceTitleAccessor="resourceTitle"
      startAccessor="start"
      endAccessor="end"
      style={{ height: '100%' }}
      view={view}
      onView={setView}
      date={date}
      onNavigate={setDate}
      views={['day', 'week', 'month']}
      step={30}
      timeslots={2}
      selectable={selectionMode}
      onSelectSlot={handleSelectSlot}
      onSelectEvent={handleSelectEvent}
      eventPropGetter={eventStyleGetter}
      components={{ event: CustomEvent }}
      messages={calendarMessages}
      formats={formats}
      defaultDate={new Date()}
      popup
      toolbar={false}
      showMultiDayTimes={true}
      min={new Date(1970, 1, 1, 0, 0, 0)}
      max={new Date(1970, 1, 1, 23, 59, 59)}
    />
  );

  return (
    <div style={{ padding: isMobile ? '0.5rem' : '1rem', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        .rbc-time-slot { border-top: 1px solid #e8e8e8 !important; }
        .rbc-time-slot:nth-child(odd) { border-top: 1px solid #d0d0d0 !important; }
        .rbc-time-slot:nth-child(even) { border-top: 1px dashed #e8e8e8 !important; }
        .rbc-timeslot-group { border-left: 1px solid #d0d0d0 !important; }
        .rbc-current-time-indicator { background-color: #e74c3c !important; height: 2px !important; }
        .rbc-event, .rbc-event-content, .rbc-event-label { overflow: hidden !important; min-width: 0 !important; }
        .rbc-event-content { width: 100% !important; }
        .rbc-event-label { text-overflow: ellipsis !important; white-space: nowrap !important; }
      `}</style>

      {/* ── Fullscreen Mode ────────────────────────────────────────────────── */}
      {isFullscreen && (
        <div style={{
          position: 'fixed',
         
          top: 'var(--navbar-height, 60px)',
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#ffffff',
          zIndex: 900, 
          display: 'flex',
          flexDirection: 'column',
          padding: '0.75rem',
          boxSizing: 'border-box',
          overflowY: 'auto',
        }}>
          {/* Fullscreen Controls */}
          <div style={{ backgroundColor: '#f8f9fa', padding: '0.75rem', borderRadius: '8px', marginBottom: '0.75rem', border: '1px solid #dee2e6' }}>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <NavControls compact />
                <ViewButtons compact />
                <Header as='h5' textAlign='center' style={{ margin: '0.25rem 0' }}>{dateLabel}</Header>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <NavControls />
                <Header as='h2' style={{ margin: 0 }}>{dateLabel}</Header>
                <ViewButtons />
              </div>
            )}
          </div>

          <div style={{ flex: 1, backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #dee2e6', overflow: 'hidden', minHeight: '300px' }}>
            <CalendarComponent />
          </div>
        </div>
      )}

      {/* ── Normal Mode ──────────────────────────────────────────────────────── */}
      {!isFullscreen && (
        <>
          {/* Header with Statistics */}
          {!isMobile ? (
            <Grid stackable style={{ marginBottom: '1rem' }}>
              <Grid.Row>
                <Grid.Column width={6}>
                  <Header as='h1' style={{ margin: 0 }}>
                    <Icon name='calendar' />
                    <Header.Content>{t('calendars.title')}</Header.Content>
                  </Header>
                </Grid.Column>
                <Grid.Column width={10}>
                  <Statistic.Group size='mini' widths='four'>
                    <Statistic color='blue'>
                      <Statistic.Value>{filteredResources.length}</Statistic.Value>
                      <Statistic.Label>{t('calendars.statistics.assets')}</Statistic.Label>
                    </Statistic>
                    <Statistic color='green'>
                      <Statistic.Value>
                        {events.filter(e => { const d = new Date(e.start); d.setHours(0,0,0,0); const t = new Date(); t.setHours(0,0,0,0); return d.getTime() === t.getTime(); }).length}
                      </Statistic.Value>
                      <Statistic.Label>{t('calendars.statistics.bookingsToday')}</Statistic.Label>
                    </Statistic>
                    <Statistic color='yellow'>
                      <Statistic.Value>
                        {events.filter(e => { const d = new Date(e.start); d.setHours(0,0,0,0); const today = new Date(); today.setHours(0,0,0,0); return d.getTime() === today.getTime() && e.isPaid === 0; }).length}
                      </Statistic.Value>
                      <Statistic.Label>{t('calendars.statistics.pendingPayment')}</Statistic.Label>
                    </Statistic>
                    <Statistic color='teal'>
                      <Statistic.Value>
                        ${events.filter(e => { const d = new Date(e.start); d.setHours(0,0,0,0); const today = new Date(); today.setHours(0,0,0,0); return d.getTime() === today.getTime() && (e.isPaid === 1 || e.isPaid === 2); }).reduce((sum, e) => sum + (e.manualPriceOverride ?? e.priceFinal ?? 0), 0)}
                      </Statistic.Value>
                      <Statistic.Label>{t('calendars.statistics.revenueToday')}</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          ) : (
            <div style={{ marginBottom: '0.5rem' }}>
              <Header as='h3' style={{ margin: '0 0 0.5rem 0' }}>
                <Icon name='calendar' /><Header.Content>{t('calendars.title')}</Header.Content>
              </Header>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'flex', gap: '0.5rem', minWidth: 'max-content' }}>
                  {[
                    { color: '#2185d0', value: filteredResources.length, label: t('calendars.statistics.assets') },
                    { color: '#21ba45', value: events.filter(e => { const d = new Date(e.start); d.setHours(0,0,0,0); const today = new Date(); today.setHours(0,0,0,0); return d.getTime() === today.getTime(); }).length, label: t('calendars.statistics.bookingsToday') },
                    { color: '#fbbd08', value: events.filter(e => { const d = new Date(e.start); d.setHours(0,0,0,0); const today = new Date(); today.setHours(0,0,0,0); return d.getTime() === today.getTime() && e.isPaid === 0; }).length, label: t('calendars.statistics.pendingPayment') },
                    { color: '#00b5ad', value: `$${events.filter(e => { const d = new Date(e.start); d.setHours(0,0,0,0); const today = new Date(); today.setHours(0,0,0,0); return d.getTime() === today.getTime() && (e.isPaid === 1 || e.isPaid === 2); }).reduce((sum, e) => sum + (e.manualPriceOverride ?? e.priceFinal ?? 0), 0)}`, label: t('calendars.statistics.revenueToday') },
                  ].map((stat, i) => (
                    <div key={i} style={{ backgroundColor: stat.color, color: 'white', padding: '0.5rem', borderRadius: '4px', minWidth: '70px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{stat.value}</div>
                      <div style={{ fontSize: '0.65rem' }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div style={{ backgroundColor: '#ffffff', padding: isMobile ? '0.5rem' : '0.8rem', borderRadius: '8px', marginBottom: isMobile ? '0.5rem' : '1rem', border: '1px solid #dee2e6' }}>
            <div onClick={() => setShowLegend(!showLegend)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1rem' }}>{t('calendars.statusLegend')}</h4>
              <Icon name={showLegend ? 'chevron up' : 'chevron down'} />
            </div>
            {showLegend && (
              <Label.Group size='small' style={{ marginTop: '0.5rem' }}>
                {localizedCalendarStatusesOptions.map(s => (
                  <Label key={s.value} style={{ backgroundColor: s.color, color: 'white', marginBottom: '0.2rem', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>{s.label}</Label>
                ))}
              </Label.Group>
            )}
          </div>

          {/* Filters */}
          <div style={{ backgroundColor: '#ffffff', padding: isMobile ? '0.5rem' : '0.8rem', borderRadius: '8px', marginBottom: isMobile ? '0.5rem' : '1rem', border: '1px solid #dee2e6' }}>
            <div onClick={() => setShowFilters(!showFilters)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showFilters ? '0.5rem' : 0 }}>
              <h4 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1rem' }}>{t('calendars.filters.title')}</h4>
              <Icon name={showFilters ? 'chevron up' : 'chevron down'} />
            </div>
            {showFilters && (
              isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Form.Field><label style={{ fontSize: '0.85rem' }}>{t('calendars.filters.venues')}</label><Dropdown placeholder={t('calendars.filters.selectVenues')} fluid multiple selection options={venueOptions} value={filters.venues} onChange={(_, { value }) => setFilters(prev => ({ ...prev, venues: value as string[] }))} style={{ zIndex: 1000 }} /></Form.Field>
                  <Form.Field><label style={{ fontSize: '0.85rem' }}>{t('calendars.filters.assetTypes')}</label><Dropdown placeholder={t('calendars.filters.selectAssetTypes')} fluid multiple selection options={sportOptions} value={filters.assetTypes} onChange={(_, { value }) => setFilters(prev => ({ ...prev, assetTypes: value as string[] }))} style={{ zIndex: 999 }} /></Form.Field>
                  <Form.Field><label style={{ fontSize: '0.85rem' }}>{t('calendars.filters.companies')}</label><Dropdown placeholder={t('calendars.filters.selectCompanies')} fluid multiple selection options={companyOptions} value={filters.companies} onChange={(_, { value }) => setFilters(prev => ({ ...prev, companies: value as string[] }))} style={{ zIndex: 998 }} /></Form.Field>
                  <Button fluid color='grey' size='small' onClick={clearFilters}>{t('calendars.filters.clearAll')}</Button>
                </div>
              ) : (
                <Grid stackable>
                  <Grid.Row>
                    <Grid.Column width={4}><Form.Field><label>{t('calendars.filters.venues')}</label><Dropdown placeholder={t('calendars.filters.selectVenues')} fluid multiple selection options={venueOptions} value={filters.venues} onChange={(_, { value }) => setFilters(prev => ({ ...prev, venues: value as string[] }))} style={{ zIndex: 1000 }} /></Form.Field></Grid.Column>
                    <Grid.Column width={4}><Form.Field><label>{t('calendars.filters.assetTypes')}</label><Dropdown placeholder={t('calendars.filters.selectAssetTypes')} fluid multiple selection options={sportOptions} value={filters.assetTypes} onChange={(_, { value }) => setFilters(prev => ({ ...prev, assetTypes: value as string[] }))} style={{ zIndex: 999 }} /></Form.Field></Grid.Column>
                    <Grid.Column width={4}><Form.Field><label>{t('calendars.filters.companies')}</label><Dropdown placeholder={t('calendars.filters.selectCompanies')} fluid multiple selection options={companyOptions} value={filters.companies} onChange={(_, { value }) => setFilters(prev => ({ ...prev, companies: value as string[] }))} style={{ zIndex: 998 }} /></Form.Field></Grid.Column>
                    <Grid.Column width={4}><Form.Field><label>&nbsp;</label><Button fluid color='grey' onClick={clearFilters}>{t('calendars.filters.clearAll')}</Button></Form.Field></Grid.Column>
                  </Grid.Row>
                </Grid>
              )
            )}
          </div>

          {/* Booking Controls */}
          <div style={{ backgroundColor: '#f8f9fa', padding: isMobile ? '0.5rem' : '1rem', borderRadius: '8px', marginBottom: isMobile ? '0.5rem' : '1rem', border: '1px solid #dee2e6' }}>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <NavControls compact />
                <ViewButtons compact />
                <Header as='h5' textAlign='center' style={{ margin: '0.25rem 0' }}>{dateLabel}</Header>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <Form.Field><label style={{ fontSize: '0.85rem' }}>{t('calendars.booking.duration')}</label><Dropdown fluid selection options={durationOptions} value={bookingDuration} onChange={(_, { value }) => setBookingDuration(Number(value))} style={{ zIndex: 997 }} /></Form.Field>
                  <Form.Field><label style={{ fontSize: '0.85rem' }}>{t('calendars.booking.status')}</label><Dropdown placeholder={t('calendars.modal.selectStatus')} fluid selection options={localizedCalendarStatusesOptions.map(s => ({ key: s.value, value: s.value, text: s.label }))} value={selectedStatus} onChange={(_, { value }) => setSelectedStatus(value as string)} style={{ zIndex: 996 }} /></Form.Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <Button fluid size='small' color={selectionMode ? 'green' : 'grey'} onClick={toggleSelectionMode} icon={selectionMode ? 'checkmark' : 'calendar plus'} content={selectionMode ? t('calendars.booking.bookingModeOn') : t('calendars.booking.enableBookingMode')} />
                  <Button fluid size='small' color='blue' onClick={() => { resetForm(); setEventForm(prev => ({ ...prev, note: buildDefaultNote(bookingDuration) })); setShowEventModal(true); }} icon='plus' content={t('calendars.booking.manualBooking')} />
                </div>
                {selectionMode && <Message info size='mini' style={{ marginBottom: 0 }}><Icon name='info circle' /><strong style={{ fontSize: '0.8rem' }}>{t('calendars.booking.quickModeInfo', { duration: bookingDuration, statusText: selectedStatus ? ` (${localizedCalendarStatusesOptions.find(s => s.value === selectedStatus)?.label})` : '' })}</strong></Message>}
              </div>
            ) : (
              <>
                <Grid stackable>
                  <Grid.Row>
                    <Grid.Column width={4}><Form.Field><label>{t('calendars.booking.duration')}</label><Dropdown fluid selection options={durationOptions} value={bookingDuration} onChange={(_, { value }) => setBookingDuration(Number(value))} style={{ zIndex: 997 }} /></Form.Field></Grid.Column>
                    <Grid.Column width={4}><Form.Field><label>{t('calendars.booking.status')}</label><Dropdown placeholder={t('calendars.modal.selectStatus')} fluid selection options={localizedCalendarStatusesOptions.map(s => ({ key: s.value, value: s.value, text: s.label }))} value={selectedStatus} onChange={(_, { value }) => setSelectedStatus(value as string)} style={{ zIndex: 996 }} /></Form.Field></Grid.Column>
                    <Grid.Column width={4}><Form.Field><label>&nbsp;</label><Button fluid color={selectionMode ? 'green' : 'grey'} onClick={toggleSelectionMode} icon={selectionMode ? 'checkmark' : 'calendar plus'} content={selectionMode ? t('calendars.booking.bookingModeOn') : t('calendars.booking.enableBookingMode')} /></Form.Field></Grid.Column>
                    <Grid.Column width={4}>
                      <Form.Field>
                        <label>&nbsp;</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button fluid color='blue' onClick={() => { resetForm(); setEventForm(prev => ({ ...prev, note: buildDefaultNote(bookingDuration) })); setShowEventModal(true); }} icon='plus' content={t('calendars.booking.manualBooking')} style={{ flex: 1 }} />
                          <Button color='teal' onClick={() => { setRecurringInitialAssetId(undefined); setRecurringInitialSlotStart(undefined); setRecurringInitialSlotEnd(undefined); setShowRecurringModal(true); }} icon='repeat' title={t('recurring.button.openModal')} />
                          <Button color='blue' onClick={toggleFullscreen} icon='expand' title='Fullscreen' />
                        </div>
                      </Form.Field>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                {/* Desktop nav controls (previous/today/next + view buttons) */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <NavControls />
                  <Header as='h3' style={{ margin: 0 }}>{dateLabel}</Header>
                  <ViewButtons />
                </div>
                {selectionMode && <Message info size='small' style={{ marginTop: '1rem', marginBottom: 0 }}><Icon name='info circle' /><strong>{t('calendars.booking.quickModeInfo', { duration: bookingDuration, statusText: selectedStatus ? ` (${localizedCalendarStatusesOptions.find(s => s.value === selectedStatus)?.label})` : '' })}</strong></Message>}
              </>
            )}
          </div>

          {/* Calendar */}
          <div style={{ flex: 1, backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #dee2e6', overflow: 'hidden', minHeight: isMobile ? '400px' : '500px' }}>
            <CalendarComponent />
          </div>
        </>
      )}

      {/* ── Capacity Details Modal (Semantic UI Modal — auto-centered, z-index OK) ── */}
      {showCapacityModal && selectedTimeSlot && (
        <Modal open={showCapacityModal} onClose={() => setShowCapacityModal(false)} size="large" style={{ maxWidth: '95vw', boxSizing: 'border-box' }}>
          <Modal.Header><Icon name="grid layout" />{t('calendar.capacityDetails')}</Modal.Header>
          <Modal.Content scrolling>
            {(() => {
              const resource = resources.find(r => r.id === selectedTimeSlot.assetId);
              if (!resource) return null;
              const occupancy = getTimeSlotOccupancy(selectedTimeSlot.assetId, selectedTimeSlot.startTime, selectedTimeSlot.endTime);
              if (!occupancy) return null;

              return (
                <>
                  <Segment color="blue">
                    <Grid columns={3} divided stackable>
                      <Grid.Row>
                        <Grid.Column textAlign="center"><Statistic size="small"><Statistic.Value>{resource.totalCapacityUnits}</Statistic.Value><Statistic.Label>{t('capacity.totalCapacity')}</Statistic.Label></Statistic></Grid.Column>
                        <Grid.Column textAlign="center"><Statistic size="small" color="red"><Statistic.Value>{occupancy.usedCapacity}</Statistic.Value><Statistic.Label>{t('capacity.used')}</Statistic.Label></Statistic></Grid.Column>
                        <Grid.Column textAlign="center"><Statistic size="small" color="green"><Statistic.Value>{resource.totalCapacityUnits - occupancy.usedCapacity}</Statistic.Value><Statistic.Label>{t('capacity.available')}</Statistic.Label></Statistic></Grid.Column>
                      </Grid.Row>
                    </Grid>
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <Label size="large" style={{ backgroundColor: occupancy.occupancyColor, color: 'white' }}>{occupancy.occupancyPercentage}% {getOccupancyLabel(occupancy.occupancyPercentage, t)}</Label>
                    </div>
                  </Segment>

                  <Header as="h4" dividing>{t('calendar.bookingsInThisSlot')} ({occupancy.bookings.length})</Header>

                  <div style={{ overflowX: 'auto' }}>
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>{t('calendar.customer')}</Table.HeaderCell>
                          <Table.HeaderCell>{t('calendar.contact')}</Table.HeaderCell>
                          <Table.HeaderCell>{t('capacity.units')}</Table.HeaderCell>
                          <Table.HeaderCell>{t('pricing.finalPrice')}</Table.HeaderCell>
                          <Table.HeaderCell>{t('calendar.payment')}</Table.HeaderCell>
                          <Table.HeaderCell>{t('common.actions')}</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {occupancy.bookings.map(booking => {
                          const liveBooking = calendarStore.events.find(e => e.id === booking.id);
                          const isDetached = liveBooking?.isDetachedFromSeries ?? booking.isDetachedFromSeries;
                          const isRecurring = !!(booking.recurringSeriesId && !isDetached);
                          return (
                            <Table.Row key={booking.id}>
                              <Table.Cell>
                                <strong>{booking.customerFullName || t('calendar.anonymous')}</strong>
                                {isRecurring && <Label size="mini" color="teal" style={{ marginLeft: '0.4rem' }}><Icon name="repeat" />{t('recurring.eventBadge.label')}</Label>}
                              </Table.Cell>
                              <Table.Cell>
                                {booking.customerPhone && <div><Icon name="phone" /> {booking.customerPhone}</div>}
                                {booking.customerEmail && <div><Icon name="mail" /> {booking.customerEmail}</div>}
                              </Table.Cell>
                              <Table.Cell><Label color="blue">{booking.capacityUsed} {t('capacity.units')}</Label></Table.Cell>
                              <Table.Cell>
                                {(() => {
                                  const effectivePrice = booking.manualPriceOverride ?? booking.priceFinal;
                                  const hasOverride = booking.manualPriceOverride != null;
                                  if (effectivePrice == null) return <span style={{ color: '#aaa' }}>—</span>;
                                  return (
                                    <div>
                                      <strong style={{ color: hasOverride ? '#e67e22' : undefined }}>${effectivePrice.toFixed(2)}</strong>
                                      {hasOverride && <div style={{ fontSize: '0.78em', color: '#e67e22' }}><Icon name="edit outline" size="small" />{t('calendar.overrideIndicator')}<div style={{ color: '#aaa', textDecoration: 'line-through' }}>${booking.priceFinal?.toFixed(2)}</div></div>}
                                    </div>
                                  );
                                })()}
                              </Table.Cell>
                              <Table.Cell>
                                {booking.isPaid === 0 && <Label color="grey"><Icon name="clock outline" />{t('payment.notPaid')}</Label>}
                                {booking.isPaid === 1 && <Label color="blue"><Icon name="credit card" />{t('payment.paidByCard')}</Label>}
                                {booking.isPaid === 2 && <Label color="green"><Icon name="money bill alternate" />{t('payment.paidWithCash')}</Label>}
                              </Table.Cell>
                              <Table.Cell>
                                <Button size="tiny" color="blue" icon="edit" title={t('common.edit')} onClick={() => { setShowCapacityModal(false); if (isRecurring) { setRecurringDialogEvent(booking); setRecurringDialogIntent('edit'); setShowRecurringDialog(true); } else { openEditForm(booking); } }} />
                                <Button size="tiny" color="red" icon="trash" style={{ marginLeft: '0.25rem' }} title={t('common.delete')} onClick={() => { setShowCapacityModal(false); if (isRecurring) { setRecurringDialogEvent(booking); setRecurringDialogIntent('delete'); setShowRecurringDialog(true); } else { setSelectedEvent(booking); setIsEditMode(true); setShowDeleteConfirm(true); } }} />
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>
                </>
              );
            })()}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setShowCapacityModal(false)}>{t('common.close')}</Button>
            <Button color='teal' onClick={() => { setShowCapacityModal(false); if (selectedTimeSlot) { setRecurringInitialAssetId(selectedTimeSlot.assetId); setRecurringInitialSlotStart(selectedTimeSlot.startTime); setRecurringInitialSlotEnd(selectedTimeSlot.endTime); setShowRecurringModal(true); } }}><Icon name="repeat" />{t('recurring.button.createSeriesFromSlot')}</Button>
            <Button primary onClick={() => { setShowCapacityModal(false); if (selectedTimeSlot) handleAddAnotherBooking(selectedTimeSlot.assetId, selectedTimeSlot.startTime, selectedTimeSlot.endTime); }}><Icon name="plus" />{t('calendar.addAnotherBooking')}</Button>
          </Modal.Actions>
        </Modal>
      )}

      {/* ── Event Modal ─────────────────────────────────────────────────────── */}
      {showEventModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 6000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: isMobile ? 'flex-end' : 'center',
            paddingTop: isMobile ? 'var(--navbar-height, 0px)' : '0',
            padding: isMobile ? 'var(--navbar-height, 0px) 0 0 0' : '15px',
            boxSizing: 'border-box',
            overflowY: 'auto',
          }}
          onClick={() => { if (!isMobile) { setShowEventModal(false); resetForm(); calendarStore.clearPriceCalculation(); } }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: isMobile ? '1.25rem 1rem' : '1.5rem',
              borderRadius: isMobile ? '16px 16px 0 0' : '8px',
              width: '100%',
              maxWidth: isMobile ? '100%' : 'min(900px, 95vw)',
              maxHeight: isMobile ? `calc(100dvh - var(--navbar-height, 0px))` : '95vh',
              overflowY: 'auto',
              boxSizing: 'border-box',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? '1rem' : '1.5rem', paddingBottom: '0.75rem', borderBottom: '2px solid #dee2e6' }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? '1.15rem' : '1.5rem', fontWeight: 'bold', color: '#2c3e50' }}>
                {isEditMode ? t('calendars.modal.editBooking') : t('calendars.modal.createNewBooking')}
                {pendingDetachEventId && <Label size="tiny" color="orange" style={{ marginLeft: '0.75rem', verticalAlign: 'middle' }}><Icon name="unlink" />{t('recurring.action.editingDetached')}</Label>}
              </h2>
              <Button circular icon='close' size={isMobile ? 'small' : 'medium'} onClick={() => { setShowEventModal(false); resetForm(); calendarStore.clearPriceCalculation(); }} style={{ margin: 0, flexShrink: 0 }} />
            </div>

            {/* Form Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '1rem' : '1.25rem' }}>
              <Form.Field>
                <label style={{ fontWeight: 'bold', fontSize: isMobile ? '0.95rem' : '1rem', marginBottom: '0.4rem', display: 'block', color: '#34495e' }}>{t('calendars.modal.asset')} *</label>
                <select
                  value={eventForm.companySportAssetVenueId}
                  onChange={(e) => { setEventForm(prev => ({ ...prev, companySportAssetVenueId: e.target.value, capacityUnitsRequested: 0, manualPriceOverride: null })); calendarStore.clearPriceCalculation(); }}
                  style={{ width: '100%', padding: isMobile ? '0.75rem' : '0.65rem', border: '1.5px solid #ced4da', borderRadius: '6px', fontSize: isMobile ? '1rem' : '0.95rem', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', boxSizing: 'border-box' }}
                  disabled={isEditMode}
                >
                  <option value="">{t('calendars.modal.selectAsset')}</option>
                  {filteredResources.map(resource => (
                    <option key={resource.id} value={resource.id}>{resource.name} - {resource.venueName} ({resource.totalCapacityUnits} {t('capacity.units')})</option>
                  ))}
                </select>
              </Form.Field>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '1rem' : '1.25rem' }}>
                <CustomDateTimePicker label={t('calendars.modal.startTime')} value={eventForm.dateTimeStart} onChange={(date) => { setEventForm(prev => ({ ...prev, dateTimeStart: date || new Date(), capacityUnitsRequested: 0, manualPriceOverride: null })); calendarStore.clearPriceCalculation(); }} />
                <CustomDateTimePicker label={t('calendars.modal.endTime')} value={eventForm.dateTimeEnd} onChange={(date) => { setEventForm(prev => ({ ...prev, dateTimeEnd: date || new Date(), capacityUnitsRequested: 0, manualPriceOverride: null })); calendarStore.clearPriceCalculation(); }} isEndTime={true} startDateTime={eventForm.dateTimeStart} minGapMinutes={30} />
              </div>

              <CapacitySelector resource={selectedResource} selectedCapacity={eventForm.capacityUnitsRequested} onCapacityChange={(capacity) => { setEventForm(prev => ({ ...prev, capacityUnitsRequested: capacity, manualPriceOverride: null })); setCapacityManuallyChanged(true); }} availableCapacity={availableCapacity} required={true} />

              {eventForm.companySportAssetVenueId && eventForm.capacityUnitsRequested > 0 && eventForm.dateTimeStart && eventForm.dateTimeEnd && (
                <PriceCalculator assetId={eventForm.companySportAssetVenueId} dateTimeStart={eventForm.dateTimeStart} dateTimeEnd={eventForm.dateTimeEnd} capacityUnitsRequested={eventForm.capacityUnitsRequested} autoCalculate={true} bookingId={isEditMode && selectedEvent ? selectedEvent.id : undefined} manualPriceOverride={eventForm.manualPriceOverride} onManualPriceOverrideChange={(value) => setEventForm(prev => ({ ...prev, manualPriceOverride: value }))} />
              )}

              <Form.Field>
                <label style={{ fontWeight: 'bold', fontSize: isMobile ? '0.95rem' : '1rem', marginBottom: '0.4rem', display: 'block', color: '#34495e' }}>{t('calendars.modal.status')} *</label>
                <select value={eventForm.statusId} onChange={(e) => setEventForm(prev => ({ ...prev, statusId: e.target.value }))} style={{ width: '100%', padding: isMobile ? '0.75rem' : '0.65rem', border: '1.5px solid #ced4da', borderRadius: '6px', fontSize: isMobile ? '1rem' : '0.95rem', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', boxSizing: 'border-box' }}>
                  <option value="">{t('calendars.modal.selectStatus')}</option>
                  {localizedCalendarStatusesOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Form.Field>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: isMobile ? '1rem' : '1.25rem' }}>
                <Form.Field>
                  <label style={{ fontWeight: 'bold', fontSize: isMobile ? '0.95rem' : '1rem', marginBottom: '0.4rem', display: 'block', color: '#34495e' }}>{t('calendars.modal.customerFullName')}</label>
                  <input type="text" value={eventForm.customerFullName} onChange={(e) => setEventForm(prev => ({ ...prev, customerFullName: e.target.value }))} style={{ width: '100%', padding: isMobile ? '0.75rem' : '0.65rem', border: '1.5px solid #ced4da', borderRadius: '6px', fontSize: isMobile ? '1rem' : '0.95rem', boxSizing: 'border-box' }} placeholder={t('calendars.modal.placeholders.fullName')} />
                </Form.Field>
                <Form.Field>
                  <label style={{ fontWeight: 'bold', fontSize: isMobile ? '0.95rem' : '1rem', marginBottom: '0.4rem', display: 'block', color: '#34495e' }}>{t('calendars.modal.customerPhone')} *</label>
                  <input type="tel" value={eventForm.customerPhone} onChange={(e) => setEventForm(prev => ({ ...prev, customerPhone: e.target.value }))} style={{ width: '100%', padding: isMobile ? '0.75rem' : '0.65rem', border: `1.5px solid ${eventForm.customerPhone.trim() ? '#ced4da' : '#dc3545'}`, borderRadius: '6px', fontSize: isMobile ? '1rem' : '0.95rem', boxSizing: 'border-box' }} placeholder={t('calendars.modal.placeholders.phone')} />
                </Form.Field>
                <Form.Field>
                  <label style={{ fontWeight: 'bold', fontSize: isMobile ? '0.95rem' : '1rem', marginBottom: '0.4rem', display: 'block', color: '#34495e' }}>{t('calendars.modal.customerEmail')} *</label>
                  <input type="email" value={eventForm.customerEmail} onChange={(e) => setEventForm(prev => ({ ...prev, customerEmail: e.target.value }))} style={{ width: '100%', padding: isMobile ? '0.75rem' : '0.65rem', border: `1.5px solid ${eventForm.customerEmail.trim() ? '#ced4da' : '#dc3545'}`, borderRadius: '6px', fontSize: isMobile ? '1rem' : '0.95rem', boxSizing: 'border-box' }} placeholder={t('calendars.modal.placeholders.email')} />
                </Form.Field>
              </div>

              {(!eventForm.customerPhone.trim() || !eventForm.customerEmail.trim()) && (
                <Message warning size="tiny" style={{ marginTop: '-0.25rem' }}><Icon name="exclamation triangle" />{t('calendars.modal.phoneEmailRequired')}</Message>
              )}

              <Form.Field>
                <label style={{ fontWeight: 'bold', fontSize: isMobile ? '0.95rem' : '1rem', marginBottom: '0.4rem', display: 'block', color: '#34495e' }}>{t('calendars.modal.note')} *</label>
                <textarea value={eventForm.note} onChange={(e) => setEventForm(prev => ({ ...prev, note: e.target.value }))} rows={isMobile ? 3 : 2} style={{ width: '100%', padding: isMobile ? '0.75rem' : '0.65rem', border: `1.5px solid ${eventForm.note.trim() ? '#ced4da' : '#dc3545'}`, borderRadius: '6px', resize: 'vertical', fontSize: isMobile ? '1rem' : '0.95rem', fontFamily: 'inherit', boxSizing: 'border-box' }} placeholder={t('calendars.modal.placeholders.notes')} />
                {!eventForm.note.trim() && <Message warning size="tiny" style={{ marginTop: '0.25rem' }}><Icon name="exclamation triangle" />{t('calendars.modal.noteRequired')}</Message>}
              </Form.Field>

              <Form.Field>
                <label style={{ fontWeight: 'bold', fontSize: isMobile ? '0.95rem' : '1rem', marginBottom: '0.4rem', display: 'block', color: '#34495e' }}>{t('calendars.modal.paymentStatus')}</label>
                <PaymentStatusSelector value={eventForm.isPaid} onChange={(value) => setEventForm(prev => ({ ...prev, isPaid: value }))} fluid />
              </Form.Field>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', marginTop: isMobile ? '1.5rem' : '2rem', gap: isMobile ? '0.75rem' : '1rem', paddingTop: isMobile ? '1rem' : '0', borderTop: isMobile ? '1px solid #dee2e6' : 'none' }}>
              {isEditMode && (
                <Button fluid={isMobile} color='red' size={isMobile ? 'large' : 'medium'} onClick={() => setShowDeleteConfirm(true)} disabled={calendarStore.loading} style={{ order: isMobile ? 3 : 1, minHeight: isMobile ? '48px' : 'auto' }}>
                  <Icon name='trash' />{t('calendars.modal.delete')}
                </Button>
              )}
              <div style={{ display: 'flex', gap: isMobile ? '0.75rem' : '0.5rem', flex: isMobile ? 'none' : 1, justifyContent: isMobile ? 'stretch' : 'flex-end', order: isMobile ? 1 : 2 }}>
                <Button fluid={isMobile} color='grey' size={isMobile ? 'large' : 'medium'} onClick={() => { setShowEventModal(false); resetForm(); calendarStore.clearPriceCalculation(); }} style={{ flex: isMobile ? 1 : 'none', minHeight: isMobile ? '48px' : 'auto' }}>
                  {t('calendars.modal.cancel')}
                </Button>
                <Button fluid={isMobile} color='blue' size={isMobile ? 'large' : 'medium'} onClick={handleSaveEvent} disabled={calendarStore.loading || !eventForm.companySportAssetVenueId || !eventForm.statusId || !eventForm.capacityUnitsRequested || eventForm.capacityUnitsRequested <= 0 || !formValid} style={{ flex: isMobile ? 1 : 'none', minHeight: isMobile ? '48px' : 'auto' }}>
                  {calendarStore.loading ? <><Icon loading name='spinner' />{t('calendars.modal.saving')}</> : <><Icon name={isEditMode ? 'save' : 'plus'} />{isEditMode ? t('calendars.modal.update') : t('calendars.modal.create')}</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 6001, /* iznad event modala */
          display: 'flex',
          justifyContent: 'center',
          alignItems: isMobile ? 'flex-end' : 'center',
          paddingTop: isMobile ? 'var(--navbar-height, 0px)' : '0',
          boxSizing: 'border-box',
        }}>
          <div style={{ backgroundColor: 'white', padding: isMobile ? '1.5rem 1rem' : '2rem', borderRadius: isMobile ? '16px 16px 0 0' : '8px', width: '100%', maxWidth: isMobile ? '100%' : 'min(400px, 95vw)', textAlign: 'center', boxSizing: 'border-box' }}>
            <Icon name='exclamation triangle' size='huge' style={{ color: '#dc3545', marginBottom: '1rem' }} />
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc3545', fontSize: isMobile ? '1.25rem' : '1.5rem' }}>{t('calendars.deleteConfirmation.title')}</h3>
            <p style={{ margin: '1rem 0', fontSize: isMobile ? '1rem' : '0.95rem', color: '#6c757d' }}>{t('calendars.deleteConfirmation.message')}</p>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '0.75rem' : '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
              <Button fluid={isMobile} color='grey' size={isMobile ? 'large' : 'medium'} onClick={() => setShowDeleteConfirm(false)} style={{ minHeight: isMobile ? '48px' : 'auto' }}>{t('calendars.deleteConfirmation.cancel')}</Button>
              <Button fluid={isMobile} color='red' size={isMobile ? 'large' : 'medium'} onClick={handleDeleteEvent} disabled={calendarStore.loading} style={{ minHeight: isMobile ? '48px' : 'auto' }}>
                {calendarStore.loading ? <><Icon loading name='spinner' />{t('calendars.modal.deleting')}</> : <><Icon name='trash' />{t('calendars.deleteConfirmation.yesDelete')}</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Recurring Modals ─────────────────────────────────────────────── */}
      <RecurringSeriesModal open={showRecurringModal} onClose={() => setShowRecurringModal(false)} initialAssetId={recurringInitialAssetId} initialSlotStart={recurringInitialSlotStart} initialSlotEnd={recurringInitialSlotEnd} statusOptions={localizedCalendarStatusesOptions} />

      <RecurringActionDialog
        open={showRecurringDialog}
        onClose={() => { setShowRecurringDialog(false); setRecurringDialogEvent(null); }}
        event={recurringDialogEvent}
        intent={recurringDialogIntent}
        onThisOnly={(event) => { setPendingDetachEventId(event.id); openEditForm(event); }}
        onSeriesDeleted={() => {}}
        statusOptions={localizedCalendarStatusesOptions}
      />
    </div>
  );
});

export default SportAssetCalendar;